import {SlidersIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton} from '@primer/react'

import {getReasoningEffortLabel, REASONING_EFFORT_OPTIONS, type ReasoningEffort} from '../../utils/reasoning-effort'
import styles from './ReasoningEffortPicker.module.css'

interface ReasoningEffortPickerProps {
  selectedReasoningEffort?: ReasoningEffort
  onUpdateReasoningEffort: (reasoningEffort: ReasoningEffort | undefined) => void
  // The reasoning efforts the selected model supports
  supportedReasoningEfforts: readonly string[] | undefined
  // Collapse the trigger to just the icon when horizontal space is tight.
  iconOnly?: boolean
}

export function ReasoningEffortPicker({
  selectedReasoningEffort,
  onUpdateReasoningEffort,
  supportedReasoningEfforts,
  iconOnly = false,
}: ReasoningEffortPickerProps) {
  // Hide until we know the model supports reasoning
  if (!supportedReasoningEfforts || supportedReasoningEfforts.length === 0) {
    return null
  }

  const options = REASONING_EFFORT_OPTIONS.filter(option => supportedReasoningEfforts.includes(option.value))

  // No explicit selection is "Default": send no override and let the backend pick the
  // model's own default effort (model-specific, not always medium).
  const triggerLabel = `Reasoning effort: ${getReasoningEffortLabel(selectedReasoningEffort)}`

  return (
    <ActionMenu>
      {iconOnly ? (
        // Icon-only (tight layouts, e.g. the global chat overlay): a square IconButton with
        // no dropdown caret or label padding. Its aria-label supplies both the accessible
        // name and the hover tooltip ("Reasoning effort: …").
        <ActionMenu.Anchor>
          <IconButton icon={SlidersIcon} aria-label={triggerLabel} variant="invisible" size="medium" />
        </ActionMenu.Anchor>
      ) : (
        // Text mode: the visible level is shown; aria-label adds the "Reasoning effort"
        // context as the accessible name. No description tooltip (it would just repeat it).
        <ActionMenu.Button className={styles.menuButton} variant="invisible" aria-label={triggerLabel}>
          <span className={styles.buttonName}>{getReasoningEffortLabel(selectedReasoningEffort)}</span>
        </ActionMenu.Button>
      )}
      <ActionMenu.Overlay width="small" align="end">
        <ActionList selectionVariant="single">
          <div className={styles.menuHeading}>Reasoning effort</div>
          <ActionList.Item
            selected={selectedReasoningEffort === undefined}
            onSelect={() => onUpdateReasoningEffort(undefined)}
          >
            Default
            <ActionList.Description variant="block">
              Let the model pick its default reasoning effort
            </ActionList.Description>
          </ActionList.Item>
          {options.map(option => (
            <ActionList.Item
              key={option.value}
              selected={option.value === selectedReasoningEffort}
              onSelect={() => onUpdateReasoningEffort(option.value)}
            >
              {option.label}
              <ActionList.Description variant="block">{option.description}</ActionList.Description>
            </ActionList.Item>
          ))}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
